import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { publicApi } from "../../admin/api";
import { needsInvert, resolveImage } from "../../lib/data";
import { usePageContent } from "../../lib/usePageContent";
import RevealClip from "../RevealClip";

const DEFAULTS = {
  portfolio_eyebrow: "Portafoglio",
  portfolio_title: "I nostri hotel a",
  portfolio_title_accent: "Milano.",
  portfolio_description:
    "Una selezione di hotel nel cuore e nei nodi chiave della città. 14 strutture 3 e 4 stelle pensate per business, leisure ed eventi.",
  portfolio_cta_label: "Tutti gli hotel",
  portfolio_max_items: 10,
};

/**
 * Bento grid con righe a altezza fissa (`auto-rows-[18rem]`), così tutte le card
 * si allineano correttamente indipendentemente dall'aspect-ratio.
 * Pattern ripetuto ogni 5 card:
 *   1. HERO  — col-span-8 row-span-2  (grande)
 *   2. small — col-span-4 row-span-1
 *   3. small — col-span-4 row-span-1
 *   4. wide  — col-span-6 row-span-1
 *   5. wide  — col-span-6 row-span-1
 */
const PATTERN = [
  "md:col-span-8 md:row-span-2",
  "md:col-span-4 md:row-span-1",
  "md:col-span-4 md:row-span-1",
  "md:col-span-6 md:row-span-1",
  "md:col-span-6 md:row-span-1",
];

export default function PortfolioBento() {
  const [hotels, setHotels] = useState([]);
  const c = usePageContent("home", DEFAULTS);

  useEffect(() => {
    publicApi.get("/hotels").then((r) => setHotels(r.data)).catch(() => {});
  }, []);

  const maxItems = parseInt(c.portfolio_max_items, 10) || DEFAULTS.portfolio_max_items;
  const featured = hotels.slice(0, maxItems);
  if (!featured.length) return null;

  return (
    <section
      data-testid="portfolio-bento"
      className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto"
    >
      <div className="flex items-end justify-between gap-6 mb-12">
        <div>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            {c.portfolio_eyebrow || DEFAULTS.portfolio_eyebrow}
          </div>
          <h2 className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl tracking-tightest">
            {c.portfolio_title || DEFAULTS.portfolio_title}{" "}
            <span className="text-[#C48F56]">
              {c.portfolio_title_accent || DEFAULTS.portfolio_title_accent}
            </span>
          </h2>
          <p className="mt-5 max-w-2xl text-white/60 text-lg leading-relaxed">
            {c.portfolio_description || DEFAULTS.portfolio_description}
          </p>
        </div>
        <Link
          to="/hotel"
          data-testid="portfolio-view-all"
          className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-sm hover:bg-white hover:text-black transition-colors whitespace-nowrap"
        >
          {c.portfolio_cta_label || DEFAULTS.portfolio_cta_label} <ArrowUpRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[14rem] md:auto-rows-[16rem] lg:auto-rows-[18rem]">
        {featured.map((h, i) => (
          <BentoCard
            key={h.id}
            hotel={h}
            className={PATTERN[i % PATTERN.length]}
            index={i}
          />
        ))}
      </div>
    </section>
  );
}

function BentoCard({ hotel, className, index }) {
  return (
    <RevealClip
      direction="up"
      duration={1.1}
      delay={(index % 3) * 0.1}
      className={`${className} min-h-[14rem]`}
    >
      <Link
        to={`/hotel/${hotel.slug}`}
        className="group relative block h-full w-full overflow-hidden rounded-xl border border-white/10 bg-[#121217]"
        data-testid={`bento-hotel-${hotel.slug}`}
      >
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
          style={{ backgroundImage: `url(${resolveImage(hotel.image)})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />

        {hotel.logo && (
          <img
            src={hotel.logo}
            alt={hotel.name}
            className={`absolute top-5 right-5 h-8 md:h-10 w-auto max-w-[110px] object-contain opacity-90 ${
              needsInvert(hotel.logo) ? "brightness-0 invert" : ""
            }`}
          />
        )}

        <div className="absolute bottom-0 inset-x-0 p-5 md:p-7">
          <h3 className="font-display text-xl md:text-2xl lg:text-3xl tracking-tight leading-tight">
            {hotel.name}
          </h3>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white/80">
              {"★".repeat(hotel.stars)}
            </span>
            <span className="glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white/80">
              {hotel.area}
            </span>
          </div>
          <p className="mt-3 text-white/60 text-sm max-w-md line-clamp-2">
            {hotel.tagline}
          </p>
          <div className="mt-4 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              {hotel.rooms} camere
            </span>
            <span className="h-9 w-9 rounded-full border border-white/20 grid place-items-center group-hover:bg-[#C48F56] group-hover:border-[#C48F56] group-hover:text-black transition-colors">
              <ArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </Link>
    </RevealClip>
  );
}
